import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Flame, 
  Droplets, 
  Wifi,
  Building2,
  HardHat,
  Factory,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const industries = [
  {
    id: 'electric',
    icon: Zap,
    title: 'Electric Utilities',
    description: 'Comprehensive utility locating services for electric infrastructure including transmission lines, distribution systems, and substations.',
    features: [
      'High-voltage transmission line locating',
      'Distribution network mapping',
      'Substation perimeter locating',
      'Underground cable detection'
    ],
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10'
  },
  {
    id: 'gas',
    icon: Flame,
    title: 'Gas Distribution',
    description: 'Precise locating for natural gas pipelines and distribution networks to ensure safety and prevent dangerous incidents.',
    features: [
      'Main line locating',
      'Service line detection',
      'Leak survey support',
      'Pipeline integrity mapping'
    ],
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10'
  },
  {
    id: 'water-sewer',
    icon: Droplets,
    title: 'Water & Sewer Authorities',
    description: 'Expert locating services for water mains, sewer lines, and stormwater systems supporting municipal infrastructure.',
    features: [
      'Water main locating',
      'Sewer line detection',
      'Stormwater system mapping',
      'Hydrant and valve locating'
    ],
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  {
    id: 'telecom',
    icon: Wifi,
    title: 'Telecom & Fiber Providers',
    description: 'Specialized locating for telecommunications infrastructure including fiber optic cables and communication networks.',
    features: [
      'Fiber optic cable locating',
      'Conduit system mapping',
      'Aerial to underground transitions',
      'Network infrastructure detection'
    ],
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10'
  },
  {
    id: 'municipalities',
    icon: Building2,
    title: 'Municipalities',
    description: 'Complete utility locating solutions for local governments and public works departments managing diverse infrastructure.',
    features: [
      'Multi-utility coordination',
      'Public works support',
      'Capital project assistance',
      'Emergency response services'
    ],
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10'
  },
  {
    id: 'engineering',
    icon: HardHat,
    title: 'Engineering & Construction Firms',
    description: 'Professional locating services supporting engineering design and construction projects of all sizes.',
    features: [
      'Pre-design utility surveys',
      'Construction support locating',
      'As-built verification',
      'Conflict resolution assistance'
    ],
    color: 'text-green-500',
    bgColor: 'bg-green-500/10'
  },
  {
    id: 'industrial',
    icon: Factory,
    title: 'Industrial Facilities',
    description: 'Comprehensive utility locating for industrial sites including manufacturing plants, refineries, and processing facilities.',
    features: [
      'Plant infrastructure mapping',
      'Process utility locating',
      'Facility expansion support',
      'Safety compliance documentation'
    ],
    color: 'text-red-500',
    bgColor: 'bg-red-500/10'
  }
];

export default function IndustriesPage() {
  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="industries-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Expertise</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              INDUSTRIES <span className="neon-text">WE SERVE</span>
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg">
              USG delivers specialized utility locating solutions tailored to the unique needs of each industry. 
              Our experienced teams combine advanced technology with deep sector expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-skytec-surface rounded-2xl border border-skytec-border p-8 hover:border-skytec-primary/30 transition-all group"
                data-testid={`industry-${industry.id}`}
              >
                <div className={`w-16 h-16 ${industry.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <industry.icon className={`w-8 h-8 ${industry.color}`} />
                </div>
                <h3 className="font-heading text-xl font-bold text-skytec-fg mb-3 uppercase">
                  {industry.title}
                </h3>
                <p className="text-skytec-fg/60 text-sm mb-6">
                  {industry.description}
                </p>
                <ul className="space-y-2">
                  {industry.features.map((feature, i) => (
                    <li key={i} className="flex items-start text-sm">
                      <CheckCircle className="w-4 h-4 text-skytec-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-skytec-fg/70">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-skytec-surface border-y border-skytec-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-heading text-4xl font-bold text-skytec-primary mb-2">130+</div>
              <div className="text-skytec-fg/60">Experienced Locators</div>
            </div>
            <div>
              <div className="font-heading text-4xl font-bold text-skytec-secondary mb-2">7</div>
              <div className="text-skytec-fg/60">Industries Served</div>
            </div>
            <div>
              <div className="font-heading text-4xl font-bold text-skytec-accent mb-2">24/7</div>
              <div className="text-skytec-fg/60">Emergency Response</div>
            </div>
            <div>
              <div className="font-heading text-4xl font-bold text-skytec-primary mb-2">99%</div>
              <div className="text-skytec-fg/60">Accuracy Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" data-testid="industries-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              NEED INDUSTRY-SPECIFIC SOLUTIONS?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Our team understands the unique challenges and requirements of your industry. 
              Contact us to discuss how USG can support your utility locating needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold"
                  data-testid="industries-contact-btn"
                >
                  REQUEST A CONSULTATION
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading"
                >
                  VIEW OUR SERVICES
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
